/**
 * THE ONLY approved way to render workflow state anywhere in this application.
 *
 * design-agent's constraint, restated because it is easy to break by accident: status is
 * encoded on THREE channels at once —
 *
 *   1. hue          (`--status-*-fg/bg/border`)
 *   2. border style (`--status-*-border-style`: dashed / solid / double / dotted)
 *   3. a glyph      (the icon below)
 *
 * …plus the text label. A protanope cannot separate the pending amber from the rejected
 * red on hue alone, and neither can a greyscale printout of a payroll register — which
 * is a document that genuinely gets printed and signed. The border treatment and the
 * glyph survive both.
 *
 * Consequently: never `<span className="text-error">Rejected</span>`, never a coloured
 * dot on its own, never a background colour as the sole signal. If a new domain status
 * appears, map it in `TONE_BY_STATUS` — do not invent a colour at the call site.
 */
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleDashed,
  Clock,
  Lock,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@frontend/lib/cn';
import { humanise } from '@frontend/lib/format';

/** The seven visual tones the design system defines. Domain statuses map onto these. */
export type StatusTone = 'draft' | 'pending' | 'approved' | 'rejected' | 'overdue' | 'locked' | 'cancelled';

const ICON_BY_TONE: Record<StatusTone, LucideIcon> = {
  draft: CircleDashed,
  pending: Clock,
  approved: CheckCircle2,
  rejected: XCircle,
  overdue: AlertTriangle,
  locked: Lock,
  cancelled: Ban,
};

/**
 * Every domain status string used anywhere in the app, mapped to a tone.
 *
 * Kept as one exhaustive table rather than per-module maps so a status can never render
 * as "approved green" in one screen and "pending amber" in another. Unknown values fall
 * back to `draft` and still render their own label, so a status an admin adds through a
 * lookup shows up readably instead of blank.
 */
const TONE_BY_STATUS: Record<string, StatusTone> = {
  // Generic / workflow
  DRAFT: 'draft',
  NOT_STARTED: 'draft',
  PLANNED: 'draft',
  OPEN: 'draft',
  PENDING: 'pending',
  IN_PROGRESS: 'pending',
  RUNNING: 'pending',
  SUBMITTED: 'pending',
  PENDING_APPROVAL: 'pending',
  AWAITING_APPROVAL: 'pending',
  ACTIVE: 'approved',
  COMPLETED: 'approved',
  COMPLETE: 'approved',
  APPROVED: 'approved',
  VERIFIED: 'approved',
  ACCEPTED: 'approved',
  POSTED: 'approved',
  PAID: 'approved',
  SETTLED: 'approved',
  RELEASED: 'approved',
  MATCHED: 'approved',
  CLOSED: 'locked',
  REJECTED: 'rejected',
  DECLINED: 'rejected',
  FAILED: 'rejected',
  ERROR: 'rejected',
  DISPUTED: 'rejected',
  BLOCKED: 'rejected',
  PARTIALLY_FAILED: 'rejected',
  OVERDUE: 'overdue',
  ESCALATED: 'overdue',
  EXPIRED: 'overdue',
  EXPIRING_SOON: 'overdue',
  PAST_DUE: 'overdue',
  LOCKED: 'locked',
  ON_HOLD: 'locked',
  SUSPENDED: 'locked',
  FROZEN: 'locked',
  CANCELLED: 'cancelled',
  VOID: 'cancelled',
  VOIDED: 'cancelled',
  WITHDRAWN: 'cancelled',
  ARCHIVED: 'cancelled',
  SKIPPED: 'cancelled',
  IGNORED: 'cancelled',
  ELIMINATED: 'cancelled',
  TERMINATED: 'cancelled',
  REVERSED: 'cancelled',
  WRITTEN_OFF: 'cancelled',
  // Employee lifecycle
  PRE_HIRE: 'draft',
  ONBOARDING: 'pending',
  ON_LEAVE: 'locked',
  NOTICE_PERIOD: 'overdue',
  // Work authorisation
  NOT_REQUIRED: 'approved',
  AUTHORIZED: 'approved',
  PENDING_VERIFICATION: 'pending',
  REVOKED: 'rejected',
  // Invoices / bills
  ISSUED: 'pending',
  SENT: 'pending',
  PARTIALLY_PAID: 'pending',
  UNMATCHED: 'draft',
  SUGGESTED: 'pending',
  // Leave
  PENDING_L1: 'pending',
  PENDING_L2: 'pending',
  CANCELLED_AFTER_APPROVAL: 'cancelled',
  // Milestones
  DELIVERED: 'approved',
  INVOICED: 'approved',
  // Payroll
  CALCULATED: 'pending',
  CALCULATING: 'pending',
  PENDING_FINAL_APPROVAL: 'pending',
  PROCESSING: 'pending',
  REVERSAL_PENDING: 'overdue',
  // Reimbursement
  REIMBURSED: 'approved',
  AWAITING_REIMBURSEMENT: 'pending',
  // Work-in-progress states
  REQUESTED_CHANGES: 'rejected',
  DELEGATED: 'pending',
  RECEIVED: 'approved',
  PARTIALLY_RECEIVED: 'pending',
};

/**
 * `status` is typed `string`, but the payloads it is fed from are not all validated at the
 * boundary, so a record whose status field is absent arrives as `undefined` — and
 * `undefined.toUpperCase()` threw the WHOLE TABLE into the error boundary. One missing
 * field on one row took out an entire screen, which is a wildly disproportionate failure
 * for a decorative badge.
 *
 * An absent status is reported as such rather than guessed at: `draft` would claim the
 * record is a draft, which may be false.
 */
export function toneForStatus(status: string | null | undefined): StatusTone {
  if (typeof status !== 'string' || status === '') return 'draft';
  return TONE_BY_STATUS[status.toUpperCase()] ?? 'draft';
}

export interface StatusBadgeProps {
  /**
   * The raw domain status, e.g. `SUBMITTED`. Case-insensitive.
   *
   * `null` / `undefined` is accepted because payloads do not always carry one, and a badge
   * must never be able to take a page down. It renders as "Not set", which is honest —
   * defaulting to "Draft" would assert a state the record does not have.
   */
  status: string | null | undefined;
  /** Overrides the humanised label — use when a lookup supplies the admin's own wording. */
  label?: string;
  /** Overrides the derived tone. Use sparingly and only where the domain disagrees. */
  tone?: StatusTone;
  className?: string;
  'data-testid'?: string;
}

export function StatusBadge({
  status,
  label,
  tone,
  className,
  'data-testid': testId,
}: StatusBadgeProps): JSX.Element {
  const resolved = tone ?? toneForStatus(status);
  const Icon = ICON_BY_TONE[resolved];
  const hasStatus = typeof status === 'string' && status !== '';
  const text = label ?? (hasStatus ? humanise(status) : 'Not set');

  return (
    <span
      className={cn('status-badge', className)}
      data-status={resolved}
      data-testid={testId ?? 'status-badge'}
      title={text}
    >
      {/* iconSize per components.badge.iconSize (agenterp.design.json) — 11px, not the default h-3 (12px). */}
      <Icon aria-hidden className="h-[11px] w-[11px] shrink-0" strokeWidth={2.25} />
      <span className="truncate">{text}</span>
      {/* Screen readers get the semantic state, not just the word. */}
      <span className="sr-only">{` (${resolved})`}</span>
    </span>
  );
}
